/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { Partner_Partner as EmbeddedPartner } from '__generated__/graphql-types';

import { Link, Typography2 } from '@coursera/cds-core';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { noheadshotUrl } from 'bundles/partners-graphql/constants';
import PartnerSquareLogo from 'bundles/unified-description-page-common/components/shared/PartnerSquareLogo';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import useIsMobile from 'bundles/unified-description-page-common/hooks/useIsMobile';
import { getPartnerPageUrl } from 'bundles/unified-description-page-common/utils/index';

const styles = {
  container: css`
    display: flex;
    margin-bottom: var(--cds-spacing-600);
  `,
  topInstructorBadgeContainer: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  partnerInfo: css`
    display: flex;
    flex-direction: column;
    align-items: start;
    margin-left: var(--cds-spacing-200);
  `,
  partnerName: css`
    text-decoration-color: var(--cds-color-neutral-primary);
  `,
  description: css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  `,
};

type Props = {
  partner: EmbeddedPartner;
  disableCTA?: boolean;
  trackingLocation?: SectionName;
};

const EmbeddedPartnersModalItem = ({ partner, disableCTA, trackingLocation }: Props) => {
  const track = useTracker();
  const eventingProductData = useEventingProductData();

  const isMobile = useIsMobile();

  const styledPartnerName = (
    <Typography2 component="span" variant="subtitleMedium">
      {partner.name}
    </Typography2>
  );

  const styledPartnerDescription = (
    <Typography2 component="p" variant="bodyPrimary" color="supportText" css={styles.description}>
      {partner.description}
    </Typography2>
  );

  const partnerPageUrl = getPartnerPageUrl(partner.slug);
  const onClickToTrack = (linkName: ButtonName, linkURL: string) => () => {
    track('click_button', {
      button: { name: linkName, linkURL },
      ...(trackingLocation
        ? {
            pageSection: {
              sectionName: trackingLocation,
            },
          }
        : {}),
      ...eventingProductData,
    });
  };

  return (
    <div css={styles.container}>
      <PartnerSquareLogo src={partner.squareLogo ? partner.squareLogo : noheadshotUrl} width={isMobile ? 60 : 48} />
      <div css={styles.partnerInfo}>
        {!disableCTA ? (
          <Link
            component={TrackedLink2}
            href={partnerPageUrl}
            trackingName={
              trackingLocation ? `${trackingLocation}_embedded_content_partner` : 'embedded_content_partner'
            }
            onClick={onClickToTrack('description_page_embedded_partner', partnerPageUrl)}
            css={styles.partnerName}
          >
            {styledPartnerName}
          </Link>
        ) : (
          styledPartnerName
        )}
        {styledPartnerDescription}
      </div>
    </div>
  );
};

export default EmbeddedPartnersModalItem;
